import { useCallback, useEffect, useState } from 'react';
import { useNotifications } from '../../../hooks/useNotifications';
import { FinalCustomersService } from '../../../services/finalCustomersService';
import { DOCUMENT_VALIDATE } from '../../../config/constants/documentErrorConstants';

export function useFinalCustomerForm({ finalCustomerId, loadOnStart = true }) {
  const [finalCustomerData, setFinalCustomerData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { showErrorNotification, showSuccessNotification } = useNotifications();
  const [errorsValidateDocument, setErrorsValidateDocument] = useState({});
  const [showModalValidator, setShowModalValidator] = useState(false);

  const getFinalCustomerData = useCallback(async () => {
    if (!finalCustomerId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const data = await FinalCustomersService.getById(finalCustomerId);
      setFinalCustomerData(data);
    } catch (error) {
      showErrorNotification({
        title: 'error',
        description: 'errorRetrievingRecord',
      });
    } finally {
      setIsLoading(false);
    }
  }, [finalCustomerId]);

  const updateFinalCustomer = async (formValues, newId, showMessage = true) => {
    setIsLoading(true);
    try {
      const customer = await FinalCustomersService.updateFinalCustomer(newId ?? finalCustomerId, formValues);
      if (showMessage) {
        showSuccessNotification({
          title: 'success',
          description: 'successUpdatingRecord',
        });
      }
      return customer;
    } catch (error) {
      const errorData = error?.response?.data;

      const isValidationError = Object.values(DOCUMENT_VALIDATE).some(
        (docError) => docError.message === errorData?.message,
      );

      if (isValidationError) {
        setErrorsValidateDocument(errorData);
        setShowModalValidator(true);
      } else {
        showErrorNotification({
          title: 'error',
          description: 'errorUpdatingRecord',
        });
      }

      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getFinalCustomerByIdentification = useCallback(async (identificationType, identification) => {
    setIsLoading(true);
    try {
      const patient = await FinalCustomersService.getByDocument(identificationType, identification);
      setFinalCustomerData(patient);
      return patient._id;
    } catch (error) {
      setFinalCustomerData(null);
      console.error(error);
    } finally {
      setIsLoading(false);
    }
    return null;
  }, []);

  const resetFinalCustomerData = () => {
    setFinalCustomerData(null);
  };

  const createFinalCustomer = async (formValues) => {
    setIsLoading(true);
    try {
      const customerData = await FinalCustomersService.createFinalCustomer(formValues);
      showSuccessNotification({
        title: 'success',
        description: 'successCreatingRecord',
      });
      return customerData;
    } catch (error) {
      const errorData = error?.response?.data;

      const isValidationError = Object.values(DOCUMENT_VALIDATE).some(
        (docError) => docError.message === errorData?.message,
      );

      if (isValidationError) {
        setErrorsValidateDocument(errorData);
        setShowModalValidator(true);
      } else {
        showErrorNotification({
          title: 'error',
          description: 'errorCreatingRecord',
        });
      }

      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (loadOnStart) {
      getFinalCustomerData();
    }
  }, [getFinalCustomerData, loadOnStart]);

  return {
    finalCustomerData,
    isLoading,
    updateFinalCustomer,
    createFinalCustomer,
    getFinalCustomerData,
    errorsValidateDocument,
    setShowModalValidator,
    showModalValidator,
    getFinalCustomerByIdentification,
    resetFinalCustomerData,
  };
}
